import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import {
  ShieldCheck, User, Phone, Mail, FileText, Landmark, Loader2, CheckCircle2,
  XCircle, UploadCloud, MessageSquare, Clock, Lock, BadgeCheck,
} from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/e98e0869-fa23-475f-b903-619c95eaa713/images/2ef0ba52c769dc945f037eb5d93d91ede1535b520d5177269f932d6b2875c6fe.png";

const maskCPF = (v) => v.replace(/\D/g, "").slice(0, 11)
  .replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{1,2})$/, "$1-$2");
const maskFone = (v) => v.replace(/\D/g, "").slice(0, 11)
  .replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2");

const inputCls = "w-full h-12 rounded-xl border border-slate-200 bg-slate-50/60 pl-10 pr-3 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-all focus:border-blue-600 focus:bg-white focus:ring-2 focus:ring-blue-100";

const Campo = ({ icon: Icon, label, req, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-sm font-semibold text-slate-700">{label}{req && <span className="text-blue-600"> *</span>}</label>
    <div className="relative">
      {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />}
      {children}
    </div>
  </div>
);

const UploadBox = ({ label, hint, file, onPick, testid }) => {
  const ref = useRef(null);
  return (
    <div>
      <label className="text-sm font-semibold text-slate-700">{label} <span className="text-blue-600">*</span></label>
      <button type="button" onClick={() => ref.current?.click()} data-testid={testid}
        className={`mt-1.5 flex w-full items-center gap-3 rounded-xl border-2 border-dashed p-3 text-left transition-all ${file ? "border-emerald-400 bg-emerald-50" : "border-slate-200 bg-slate-50/60 hover:border-blue-400 hover:bg-blue-50/50"}`}>
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${file ? "bg-emerald-500" : "bg-blue-600"} text-white`}>
          {file ? <CheckCircle2 className="h-5 w-5" /> : <UploadCloud className="h-5 w-5" />}
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium text-slate-700">{file ? file.name : "Selecionar arquivo"}</span>
          <span className="block text-xs text-slate-400">{file ? `${(file.size / 1024 / 1024).toFixed(1)} MB` : hint}</span>
        </span>
      </button>
      <input ref={ref} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif" className="hidden"
        onChange={(e) => onPick(e.target.files?.[0] || null)} />
    </div>
  );
};

export default function LandingServidorFederal() {
  const [form, setForm] = useState({ nome: "", telefone: "", email: "", cpf: "", observacao: "" });
  const [contracheque, setContracheque] = useState(null);
  const [extrato, setExtrato] = useState(null);
  const [captcha, setCaptcha] = useState({ captcha_id: "", pergunta: "" });
  const [captchaResp, setCaptchaResp] = useState("");
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [ok, setOk] = useState(false);

  const carregarCaptcha = () => {
    axios.get(`${API}/servidor-federal/captcha`).then((r) => { setCaptcha(r.data); setCaptchaResp(""); }).catch(() => {});
  };
  useEffect(() => { carregarCaptcha(); }, []);

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const enviar = async () => {
    setErro("");
    if (form.cpf.replace(/\D/g, "").length !== 11) return setErro("Informe um CPF válido.");
    if (!form.nome.trim()) return setErro("Informe seu nome completo.");
    if (!form.telefone.replace(/\D/g, "")) return setErro("Informe seu melhor telefone.");
    if (!contracheque) return setErro("Anexe o seu último contracheque.");
    if (!extrato) return setErro("Anexe o seu extrato de consignação.");
    if (!captchaResp.trim()) return setErro("Responda o código de verificação.");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("nome", form.nome);
      fd.append("telefone", form.telefone.replace(/\D/g, ""));
      fd.append("email", form.email);
      fd.append("cpf", form.cpf.replace(/\D/g, ""));
      fd.append("observacao", form.observacao);
      fd.append("captcha_id", captcha.captcha_id);
      fd.append("captcha_resp", captchaResp);
      fd.append("contracheque", contracheque);
      fd.append("extrato", extrato);
      await axios.post(`${API}/servidor-federal/solicitar`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOk(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setErro(e.response?.data?.detail || "Não foi possível enviar sua solicitação. Tente novamente.");
      carregarCaptcha();
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100" data-testid="landing-federal">
      {/* Topbar */}
      <header className="w-full border-b border-white/60 bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-blue-700 to-blue-500 text-white"><Landmark className="h-5 w-5" /></span>
            <span className="text-lg font-black tracking-tight text-blue-800">Stilo<span className="text-emerald-500">Cred</span></span>
          </div>
          <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
            <ShieldCheck className="h-4 w-4 text-emerald-500" /> Ambiente 100% seguro
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-4 py-8 lg:grid-cols-[1fr_1.1fr] lg:items-start">
        {/* Hero */}
        <section className="order-2 lg:order-1 lg:sticky lg:top-8">
          <div className="overflow-hidden rounded-3xl bg-gradient-to-br from-blue-800 to-blue-600 p-1 shadow-xl">
            <div className="rounded-[22px] bg-white">
              <img src={HERO_IMG} alt="Servidor Federal" className="h-56 w-full rounded-t-[22px] object-cover object-top sm:h-64" />
              <div className="p-6">
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-xs font-bold text-blue-700"><BadgeCheck className="h-3.5 w-3.5" /> Servidor Federal Civil</span>
                <h2 className="mt-3 text-2xl font-extrabold text-slate-800">Análise de crédito exclusiva 👋</h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  Envie seus dados e documentos e nossa equipe faz a <b className="text-blue-700">análise do seu crédito consignado</b>.
                  Retorno em <b>até 24h</b>, respeitando a ordem de chegada. Seus dados são mantidos em total sigilo.
                </p>
                <div className="mt-5 grid grid-cols-3 gap-3 text-center">
                  {[[Clock, "Retorno", "em até 24h"], [Lock, "Sigilo", "total dos dados"], [BadgeCheck, "Sem", "compromisso"]].map(([Ic, a, b], i) => (
                    <div key={i} className="rounded-xl bg-blue-50 p-3">
                      <Ic className="mx-auto h-5 w-5 text-blue-600" />
                      <p className="mt-1 text-xs font-bold text-slate-700">{a}</p>
                      <p className="text-[10px] text-slate-500">{b}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Form */}
        <section className="order-1 lg:order-2">
          <div className="rounded-3xl border border-white bg-white/90 p-6 shadow-xl backdrop-blur sm:p-8">
            {ok ? (
              <div className="space-y-4 py-6 text-center" data-testid="sf-sucesso">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100"><CheckCircle2 className="h-9 w-9 text-emerald-500" /></div>
                <h1 className="text-2xl font-extrabold text-slate-800">Solicitação recebida! ✅</h1>
                <p className="mx-auto max-w-md text-sm text-slate-600">
                  Recebemos seus dados e documentos. Nossa equipe de consultores fará a análise e retornará
                  em <b>até 24 horas</b>, respeitando a ordem de chegada. Obrigado!
                </p>
                <button onClick={() => { setOk(false); setForm({ nome: "", telefone: "", email: "", cpf: "", observacao: "" }); setContracheque(null); setExtrato(null); carregarCaptcha(); }}
                  className="text-sm font-semibold text-blue-600 underline" data-testid="sf-nova">Enviar outra solicitação</button>
              </div>
            ) : (
              <>
                <div className="mb-6 flex flex-col items-center text-center">
                  <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-blue-700 to-blue-500 shadow-lg shadow-blue-200">
                    <Landmark className="h-7 w-7 text-white" />
                  </div>
                  <h1 className="text-2xl font-extrabold text-slate-800 sm:text-3xl">Simulação — Servidor Federal</h1>
                  <p className="mt-1 max-w-md text-sm text-slate-500">Preencha seus dados e anexe os documentos para a análise do seu crédito consignado.</p>
                </div>

                {erro && (
                  <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="sf-erro">
                    <XCircle className="mt-0.5 h-4 w-4 shrink-0" /> {erro}
                  </div>
                )}

                <div className="space-y-4">
                  <p className="text-sm font-bold text-slate-700">Dados pessoais</p>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <Campo icon={User} label="Nome completo" req>
                        <input className={inputCls} placeholder="Digite seu nome completo" value={form.nome} onChange={(e) => setF("nome", e.target.value)} data-testid="sf-nome" />
                      </Campo>
                    </div>
                    <Campo icon={Phone} label="Seu melhor telefone" req>
                      <input className={inputCls} placeholder="(00) 00000-0000" inputMode="numeric" value={form.telefone} onChange={(e) => setF("telefone", maskFone(e.target.value))} data-testid="sf-telefone" />
                    </Campo>
                    <Campo icon={Mail} label="E-mail">
                      <input className={inputCls} placeholder="Digite seu e-mail" type="email" value={form.email} onChange={(e) => setF("email", e.target.value)} data-testid="sf-email" />
                    </Campo>
                    <Campo icon={User} label="CPF" req>
                      <input className={inputCls} placeholder="000.000.000-00" inputMode="numeric" value={form.cpf} onChange={(e) => setF("cpf", maskCPF(e.target.value))} data-testid="sf-cpf" />
                    </Campo>
                  </div>

                  <p className="pt-2 text-sm font-bold text-slate-700">Documentos para análise</p>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <UploadBox label="Último contracheque" hint="PDF ou foto (JPG/PNG)" file={contracheque} onPick={setContracheque} testid="sf-upload-contracheque" />
                    <UploadBox label="Extrato de consignação" hint="PDF ou foto (JPG/PNG)" file={extrato} onPick={setExtrato} testid="sf-upload-extrato" />
                  </div>

                  <Campo icon={MessageSquare} label="Observação">
                    <textarea rows={3} className="w-full rounded-xl border border-slate-200 bg-slate-50/60 pl-10 pr-3 pt-3 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-all focus:border-blue-600 focus:bg-white focus:ring-2 focus:ring-blue-100"
                      placeholder="Ex.: valor desejado, dúvidas, melhor horário para contato..." value={form.observacao} onChange={(e) => setF("observacao", e.target.value)} data-testid="sf-observacao" />
                  </Campo>

                  {/* Captcha simples */}
                  <div className="flex items-end gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                    <div className="flex-1">
                      <label className="text-sm font-semibold text-slate-700">Verificação: <span className="text-blue-700">{captcha.pergunta || "..."}</span> <span className="text-blue-600">*</span></label>
                      <input className="mt-1.5 h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100"
                        placeholder="Digite o resultado" inputMode="numeric" value={captchaResp} onChange={(e) => setCaptchaResp(e.target.value)} data-testid="sf-captcha" />
                    </div>
                    <button type="button" onClick={carregarCaptcha} className="h-11 rounded-lg border border-slate-200 px-3 text-xs text-slate-500 hover:bg-white" title="Gerar outro">↻</button>
                  </div>

                  <button onClick={enviar} disabled={loading} data-testid="sf-enviar-btn"
                    className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-700 to-emerald-500 text-lg font-bold text-white shadow-lg shadow-blue-200 transition-all hover:from-blue-800 hover:to-emerald-600 active:scale-[0.99] disabled:opacity-60">
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileText className="h-5 w-5" />}
                    {loading ? "Enviando..." : "Solicitar análise"}
                  </button>
                  <p className="flex items-center justify-center gap-1.5 text-center text-xs text-slate-400">
                    <ShieldCheck className="h-3.5 w-3.5" /> Todos os seus dados são mantidos em sigilo e jamais serão divulgados.
                  </p>
                </div>
              </>
            )}
          </div>
        </section>
      </main>

      <footer className="pb-8 text-center text-xs text-slate-400">StiloCred · Crédito Consignado Servidor Federal — Ambiente seguro 🔒</footer>
    </div>
  );
}
